import React from 'react';
import memoizeOne from 'memoize-one';
import { cascadingSelectCellHandler } from '@atlassian/jira-business-fields/src/ui/cascading-select/cell/index.tsx';
import { devSummaryCellHandler } from '@atlassian/jira-business-fields/src/ui/dev-summary/cell/index.tsx';
import { goalsCellHandler } from '@atlassian/jira-business-fields/src/ui/goals/cell/index.tsx';
import { groupCellHandler } from '@atlassian/jira-business-fields/src/ui/group/cell/index.tsx';
import { parentCellHandler } from '@atlassian/jira-business-fields/src/ui/parent/cell/index.tsx';
import { projectCellHandler } from '@atlassian/jira-business-fields/src/ui/project/cell/index.tsx';
import { radioSelectCellHandler } from '@atlassian/jira-business-fields/src/ui/radio-select/cell/index.tsx';
import { resolutionCellHandler } from '@atlassian/jira-business-fields/src/ui/resolution/cell/index.tsx';
import { statusCategoryCellHandler } from '@atlassian/jira-business-fields/src/ui/status-category/cell/index.tsx';
import { teamCellHandler } from '@atlassian/jira-business-fields/src/ui/team/cell/index.tsx';
import { versionCellHandler } from '@atlassian/jira-business-fields/src/ui/version/cell/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	BasicListViewItem,
	FieldCellProps,
	ListIssueFieldTransformed,
	TopLevelFieldTransformed,
} from '../common/types';

export type FieldCellsProps = { fieldType: string } & FieldCellProps<
	BasicListViewItem,
	ListIssueFieldTransformed | TopLevelFieldTransformed
>;

const createFieldCellComponents = memoizeOne(() => ({
	...versionCellHandler,
	...parentCellHandler,
	...devSummaryCellHandler,
	...(fg('native_goals_integration_on_list_view') ? goalsCellHandler : {}),
	...teamCellHandler,
	...(ff('jira.list.field-support-part1')
		? {
				...resolutionCellHandler,
				...groupCellHandler,
				...statusCategoryCellHandler,
				...radioSelectCellHandler,
				...projectCellHandler,
				...cascadingSelectCellHandler,
			}
		: {}),
}));

type FieldCellTypes = keyof ReturnType<typeof createFieldCellComponents>;

type FieldCellComponent = (
	_: FieldCellProps<BasicListViewItem, ListIssueFieldTransformed | TopLevelFieldTransformed>,
) => JSX.Element;

export const isFieldCellSupported = (fieldType: FieldCellTypes): fieldType is FieldCellTypes => {
	const Components = createFieldCellComponents();
	return Components[fieldType] != null;
};

export const isFieldCellTypes = (_: unknown): _ is FieldCellTypes => true;

export const FieldCell = ({ fieldType, ...cellProps }: FieldCellsProps) => {
	const Components = createFieldCellComponents();

	if (!isFieldCellTypes(fieldType)) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const Component = Components[fieldType] as FieldCellComponent | undefined;

	if (Component == null) {
		return null;
	}

	return <Component {...cellProps} />;
};
